
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, Users, Clock, Edit, UserCircle, CalendarX } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import type { GymClass } from '@/api/classes';
import { AddTimeSlotsDialog } from './add-time-slots-dialog';
import { TimeSlotsDialog } from './time-slots-dialog';
import { EditClassDialog } from './edit-class-dialog';
import { useStaff } from '@/api/staff';

interface ClassCardProps {
  gymClass: GymClass;
}

export function ClassCard({ gymClass }: ClassCardProps) {
  const { t } = useTranslation();
  const [addTimeSlotsOpen, setAddTimeSlotsOpen] = useState(false);
  const [viewTimeSlotsOpen, setViewTimeSlotsOpen] = useState(false);
  const [editClassOpen, setEditClassOpen] = useState(false);
  const { data: staffMembers } = useStaff();

  const totalSlots = gymClass.timeSlots?.length || 0;
  const activeSlots =
    gymClass.timeSlots?.filter((slot) => !slot.disabled)?.length || 0;

  // Get staff member details if staffMember ID exists
  const staffMember = staffMembers?.find(
    (staff) => staff._id === gymClass.staffMember || staff.id === gymClass.staffMember
  );

  // Helper to get day names
  const getDayNames = (days: number[]) => {
    const names = [
      t('classes.sunday', 'Sun'),
      t('classes.monday', 'Mon'),
      t('classes.tuesday', 'Tue'),
      t('classes.wednesday', 'Wed'),
      t('classes.thursday', 'Thu'),
      t('classes.friday', 'Fri'),
      t('classes.saturday', 'Sat'),
    ];
    return days.map((d) => names[d]).join(', ');
  };

  return (
    <>
      <Card className="hover:shadow-lg transition-shadow">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex gap-3 flex-1">
              {gymClass.image && (
                <img
                  src={gymClass.image}
                  alt={gymClass.label}
                  className="h-14 w-14 rounded-lg object-cover border shrink-0"
                />
              )}
              <div className="space-y-1 flex-1">
              <CardTitle>{gymClass.label}</CardTitle>
              <CardDescription>{gymClass.name}</CardDescription>
              
              {/* Staff Member */}
              {staffMember && (
                <div className="flex items-center gap-2 mt-2">
                  <UserCircle className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">
                    {staffMember.name} {staffMember.surname}
                  </span>
                  <Badge variant="outline" className="text-xs">
                    {staffMember.userType}
                  </Badge>
                </div>
              )}

              {/* Days of Week Schedule */}
              {gymClass.daysOfWeek && gymClass.daysOfWeek.length > 0 && (
                <div className="flex items-center gap-2 mt-1">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    {getDayNames(gymClass.daysOfWeek)}
                  </span>
                </div>
              )}

              {/* Blackout Dates */}
              {gymClass.blackoutDates && gymClass.blackoutDates.length > 0 && (
                <div className="flex items-center gap-2 mt-1">
                  <CalendarX className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    {gymClass.blackoutDates.length} {t('classes.blackoutDates', 'blackout dates')}
                  </span>
                </div>
              )}
            </div>
            </div>
            <div className="flex flex-col items-end gap-2">
              <Badge variant={gymClass.isActive ? 'default' : 'secondary'}>
                {gymClass.isActive ? t('classes.active') : t('common.inactive')}
              </Badge>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEditClassOpen(true)}
              >
                <Edit className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-muted-foreground">
                  {t('classes.timeSlots')}
                </p>
                <p className="font-medium">
                  {activeSlots} / {totalSlots}
                </p>
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1 text-xs"
              onClick={() => setAddTimeSlotsOpen(true)}
            >
              <Clock className="h-3.5 w-3.5 mr-1.5" />
              {t('classes.addTimeSlots')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="flex-1 text-xs"
              onClick={() => setViewTimeSlotsOpen(true)}
            >
              <Users className="h-3.5 w-3.5 mr-1.5" />
              {t('classes.manageTimeSlots')}
            </Button>
          </div>
        </CardContent>
      </Card>

      <AddTimeSlotsDialog
        open={addTimeSlotsOpen}
        onOpenChange={setAddTimeSlotsOpen}
        gymClass={gymClass}
      />

      <TimeSlotsDialog
        open={viewTimeSlotsOpen}
        onOpenChange={setViewTimeSlotsOpen}
        gymClass={gymClass}
      />

      <EditClassDialog
        open={editClassOpen}
        onOpenChange={setEditClassOpen}
        gymClass={gymClass}
      />
    </>
  );
}
